import * as Sentry from '@sentry/browser';
import { html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { buildURL, fetchRequest } from 'Utils/api-fetch-request';
import { EVENT_CLIENT_DISCLOSURE_RESOLVED, EVENT_PLAYER_CHAPTER_CHANGE } from 'Utils/types';
import Styles from './vm-client-disclosure.scss';

export interface DisclosureItem {
    id: number;
    name: string;
    logo: string;
    chapter_id: number | null;
}

let fetchPromise: Promise<DisclosureItem[]> | null = null;
let eventDispatched = false;
let lastShownKey: number | string | undefined;

// Module-level function so the fetch is shared across multiple component instances (mobile/desktop)
function fetchDisclosureData(videoId: string, lectureSeriesId: string, forceAdDisclosure: string | null) {
    let params;
    if (forceAdDisclosure) {
        params = { force_ad_disclosure: forceAdDisclosure };
    } else {
        params = lectureSeriesId ? { lecture_series_id: lectureSeriesId } : { video_id: videoId };
    }
    if (!fetchPromise) {
        const url = buildURL('/api/ad-disclosure/', params);
        fetchPromise = fetchRequest(url);
    }
    return fetchPromise;
}

@customElement('vm-client-disclosure')
export class VmClientDisclosure extends LitElement {
    @property({ type: String, attribute: 'video-id' })
    videoId = '';

    @property({ type: String, attribute: 'lecture-series-id' })
    lectureSeriesId = '';

    @property({ type: String, attribute: 'user-id' })
    userId = '';

    @state()
    disclosureItems: DisclosureItem[] = [];

    @state()
    dataFetched = false;

    @state()
    fetchError = false;

    @state()
    activeChapterId?: number;

    static override styles = Styles;

    private _forceAdDisclosure = new URLSearchParams(window.location.search).get('force_ad_disclosure');

    get globalDisclosureItem() {
        return this.disclosureItems.find((item) => !item.chapter_id);
    }

    get chapterDisclosureItems() {
        return this.disclosureItems.filter((item) => item.chapter_id);
    }

    get activeChapterItem() {
        return this.chapterDisclosureItems.find((item) => item.chapter_id === this.activeChapterId);
    }

    get activeDisclosureItem() {
        return this.globalDisclosureItem || this.activeChapterItem;
    }

    trackMixpanelEvent(eventName: string) {
        const item = this.activeDisclosureItem;

        if (!item || !window.vumedi?.mixpanel || this._forceAdDisclosure) return false;

        const props: Record<string, number | string> = {
            ad_disclosure_id: item.id,
            ad_disclosure_name: item.name,
        };

        if (this.lectureSeriesId) {
            props.lecture_series_id = Number(this.lectureSeriesId);
        }

        if (this.videoId) {
            props.video_id = Number(this.videoId);
        }

        if (this.activeChapterItem) {
            props.chapter_id = this.activeChapterItem.chapter_id!;
        }

        window.vumedi.mixpanel.track(eventName, props);

        return true;
    }

    // Module-level guard ensures only one instance reports per appearance
    trackShownEvent() {
        if (!this.activeDisclosureItem) {
            lastShownKey = undefined;
            return;
        }

        // Use chapter ID for chapter disclosures, 'global' for global disclosure
        // global fires once,
        // chapter disclosures fire per chapter change, even if the same disclosure is shown
        const key = this.globalDisclosureItem ? 'global' : this.activeChapterId;
        if (lastShownKey === key) return;

        // Only set the key if the event was actually sent
        if (this.trackMixpanelEvent('AdDisclosureShown')) {
            lastShownKey = key;
        }
    }

    private _handleChapterChange = (e: Event) => {
        this.activeChapterId = Number((e as CustomEvent<{ chapterId?: string }>).detail.chapterId);
    };

    override async connectedCallback() {
        super.connectedCallback();

        if (!this.userId) {
            window.dispatchEvent(new CustomEvent(EVENT_CLIENT_DISCLOSURE_RESOLVED, { detail: [] }));

            return;
        }

        window.addEventListener(EVENT_PLAYER_CHAPTER_CHANGE, this._handleChapterChange);

        try {
            this.disclosureItems = await fetchDisclosureData(
                this.videoId,
                this.lectureSeriesId,
                this._forceAdDisclosure,
            );
        } catch (error) {
            Sentry.captureException(error);
            this.fetchError = true;
        }

        if (!eventDispatched) {
            eventDispatched = true;
            if (this.fetchError || !this.globalDisclosureItem) {
                const chapterIdsForExclusion = this.chapterDisclosureItems.map((item) => item.chapter_id);
                window.dispatchEvent(
                    new CustomEvent(EVENT_CLIENT_DISCLOSURE_RESOLVED, { detail: chapterIdsForExclusion }),
                );
            }
        }

        this.dataFetched = true;
    }

    override updated() {
        this.classList.toggle('is-hidden', !this.globalDisclosureItem && !this.activeChapterItem);
        if (this.dataFetched) this.classList.add('is-resolved');

        this.trackShownEvent();
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener(EVENT_PLAYER_CHAPTER_CHANGE, this._handleChapterChange);
    }

    renderDisclosure(item: DisclosureItem) {
        return html`
            <div
                class="vm-client-disclosure__content-wrap"
                @click=${() => this.trackMixpanelEvent('AdDisclosureClicked')}
                @mouseenter=${() =>
                    window.matchMedia('(hover: hover)').matches && this.trackMixpanelEvent('AdDisclosureHovered')}
            >
                <div class="vm-client-disclosure__logo-wrap">
                    <figure class="vm-client-disclosure__logo">
                        <img src="${item.logo}" alt="${item.name}" />
                        <figcaption class="vm-client-disclosure__logo-caption">Advertisement</figcaption>
                    </figure>
                </div>
                <div class="vm-client-disclosure__text-wrap">
                    <p class="vm-client-disclosure__text">Content developed independently by authors.</p>
                </div>
            </div>
        `;
    }

    override render() {
        if (!this.dataFetched) return nothing;

        if (this.globalDisclosureItem) {
            return this.renderDisclosure(this.globalDisclosureItem);
        }

        if (this.activeChapterItem) {
            return this.renderDisclosure(this.activeChapterItem);
        }

        return nothing;
    }
}
